import { HelpCircle, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

interface HelpPopoverProps {
  /** Short title for the popover. Mirrors the page title typically. */
  title: string;
  /** Body content — string for simple text or ReactNode for rich
   *  layouts (lists, links). Kept terse: 1-3 short paragraphs is the
   *  sweet spot. */
  children: ReactNode;
}

/** Inline help affordance — a small ``?`` button next to a page
 *  heading. Click opens a small popover with contextual explanation
 *  of what the page does. Click outside or hit Escape to close.
 *
 *  Used by every long-form page that needs orientation copy
 *  (pricing rules, jobs, quote detail, etc.) without polluting the
 *  page itself with explanatory chrome. */
export function HelpPopover({ title, children }: HelpPopoverProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <div className="relative inline-block" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Help: ${title}`}
        className="inline-flex h-6 w-6 items-center justify-center rounded-full text-graphite-soft transition hover:bg-paper-warm hover:text-graphite"
      >
        <HelpCircle className="h-4 w-4" aria-hidden />
      </button>

      {open && (
        // biome-ignore lint/a11y/useSemanticElements: popover-as-dialog pattern; native <dialog> has different semantics (showModal/close lifecycle, top-layer rendering) that don't fit an inline popover.
        <div
          role="dialog"
          aria-label={title}
          className="absolute left-0 top-full z-30 mt-2 w-80 rounded-lg border border-rule bg-paper-warm p-4 text-sm shadow-lg"
        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-mono text-sm font-bold tracking-tight text-graphite">{title}</h3>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close"
              className="text-graphite-soft hover:text-graphite"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          <div className="mt-2 space-y-2 text-graphite-soft">{children}</div>
        </div>
      )}
    </div>
  );
}
